import {FC, PropsWithChildren, useCallback, useMemo, useRef} from 'react'
import {debugLogger} from '../utils/debugLogger'
import {IWakeLockContext, WakeLockContext} from './WakeLock.context'

export const WakeLockProvider: FC<PropsWithChildren> = ({children}) => {
  const wakeLock = useRef<WakeLockSentinel | null>(null)

  const requestWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator && !wakeLock.current) {
      try {
        wakeLock.current = await navigator.wakeLock.request('screen')
        debugLogger({
          message: 'Wake Lock is requested',
          tag: 'wakeLock',
        })
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error(`${err.name}, ${err.message}`)
        }
      }
    }
  }, [])

  const releaseWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator && wakeLock.current) {
      try {
        wakeLock.current?.release()
        wakeLock.current = null
        debugLogger({
          message: 'Wake Lock is released',
          tag: 'wakeLock',
        })
      } catch (err: unknown) {
        if (err instanceof Error) {
          console.error(`${err.name}, ${err.message}`)
        }
      }
    }
  }, [])

  const wakeLockContextValue: IWakeLockContext = useMemo(
    () => ({
      requestWakeLock,
      releaseWakeLock,
    }),
    [requestWakeLock, releaseWakeLock],
  )

  return (
    <WakeLockContext.Provider value={wakeLockContextValue}>{children}</WakeLockContext.Provider>
  )
}
